import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import {
  SkeletonTableRows,
} from "@ui-components/Skeleton/GenericSkeleton";
import {
  getReleases,
  deleteRelease,
  setReleaseLatest,
  ReleaseRecord,
} from "./request/request";
import CreateReleaseModal, { RELEASE_SERVICES } from "./CreateReleaseModal";
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from "../../helpers/tableViewport";

/** SideNav + title/Create row + card padding (CTX-331). */
const RELEASES_CHROME_PX = 280;

const TOTAL_SERVICES = RELEASE_SERVICES.length;

const ReleasesPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [releases, setReleases] = useState<ReleaseRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState<ReleaseRecord | null>(null);

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertAction, setAlertAction] = useState<"delete" | "setLatest" | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<ReleaseRecord | null>(null);

  const fetchReleases = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const res = await getReleases(token);
      if (res.data.success) {
        setReleases(res.data.data);
      }
    } catch (err) {
      console.error("[ReleasesPage] Error fetching releases:", err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  // -- Handlers --

  const handleCreate = () => {
    setEditingRelease(null);
    setIsModalOpen(true);
  };

  const handleEdit = (release: ReleaseRecord) => {
    setEditingRelease(release);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (release: ReleaseRecord) => {
    setSelectedRelease(release);
    setAlertAction("delete");
    setAlertOpen(true);
  };

  const handleSetLatestClick = (release: ReleaseRecord) => {
    setSelectedRelease(release);
    setAlertAction("setLatest");
    setAlertOpen(true);
  };

  const handleAlertConfirm = async () => {
    if (!selectedRelease) return;
    try {
      const token = await getAccessTokenSilently();
      if (alertAction === "delete") {
        await deleteRelease(selectedRelease.id, token);
      } else if (alertAction === "setLatest") {
        await setReleaseLatest(selectedRelease.id, token);
      }
      fetchReleases();
    } catch (err) {
      console.error("[ReleasesPage] Action error:", err);
    } finally {
      setAlertOpen(false);
      setSelectedRelease(null);
      setAlertAction(null);
    }
  };

  const handleModalClose = (refreshNeeded?: boolean) => {
    setIsModalOpen(false);
    setEditingRelease(null);
    if (refreshNeeded) {
      fetchReleases();
    }
  };

  // -- Table columns --

  const columns: GenericTableColumn<ReleaseRecord>[] = [
    {
      header: "Name",
      accessor: "name",
      sortable: true,
    },
    {
      header: "Channel",
      accessor: "channel",
      sortable: true,
    },
    {
      header: "Scope",
      accessor: "scope",
      // Widest it ever renders is "partial (12/12)". Without a width the table
      // hands leftover space to whichever column will take it, and these two
      // were taking it from the ones that need it.
      width: "140px",
      renderCell: (row: ReleaseRecord) => {
        const count = Object.keys(row.components || {}).length;
        return (
          <span>
            {row.scope} ({count}/{TOTAL_SERVICES})
          </span>
        );
      },
    },
    {
      header: "Latest",
      accessor: "isLatest" as keyof ReleaseRecord,
      // Sized by its header, not its content: the cell holds one short tag or
      // nothing at all.
      width: "90px",
      renderCell: (row: ReleaseRecord) =>
        row.isLatest ? (
          <GenericTag label="Latest" variant="success" />
        ) : null,
    },
    {
      header: "Components",
      accessor: "components" as keyof ReleaseRecord,
      // Bounded on purpose. A full release carries twelve tags, and letting
      // them wrap let this column claim whatever width it wanted — which
      // pushed Created and Actions past the viewport and put a scrollbar under
      // the whole table. The overflow now lives in the cell that causes it:
      // the tags stay on one line and scroll inside their own column, so every
      // other column keeps its place.
      width: "320px",
      renderCell: (row: ReleaseRecord) => {
        const entries = Object.entries(row.components || {});
        return (
          <div className="flex flex-nowrap gap-1 overflow-x-auto">
            {entries.map(([svc, ver]) => (
              <GenericTag key={svc} label={`${svc}: ${ver}`} variant="default" />
            ))}
          </div>
        );
      },
    },
    {
      header: "Created",
      accessor: "createdAt",
      sortable: true,
      renderCell: (row: ReleaseRecord) =>
        new Date(row.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
    },
    {
      header: "Actions",
      accessor: "id" as keyof ReleaseRecord,
      renderCell: (row: ReleaseRecord) => (
        <div className="flex gap-2">
          <GenericButton
            label="Edit"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
          />
          {!row.isLatest && (
            <>
              <GenericButton
                label="Set Latest"
                variant="brand"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetLatestClick(row);
                }}
              />
              <GenericButton
                label="Delete"
                variant="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row);
                }}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="relative">
      <GenericCard isCollapsible={false} className="flex flex-col">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Releases</h2>
            <GenericButton
              label="Create Release"
              variant="brand"
              size="md"
              onClick={handleCreate}
            />
          </div>

          {loading ? (
            <div className="px-4 pb-4">
              <SkeletonTableRows
                rows={5}
                cols={6}
                colWidths={["120px", "80px", "100px", "60px", "200px", "100px"]}
                rowHeight={15}
                rowGapClassName="space-y-3"
              />
            </div>
          ) : (
            <GenericTable
              columns={columns}
              data={releases}
              initialRowsPerPage={getInitialRowsPerPage(releases.length)}
              rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
              width="100%"
              tableClassName="bg-semantic-bg-primary"
              maxTableBodyHeight={getTableBodyMaxHeight(RELEASES_CHROME_PX)}
            />
          )}
        </div>
      </GenericCard>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <CreateReleaseModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          release={editingRelease}
        />
      )}

      {/* Delete confirmation */}
      <GenericAlert
        isOpen={alertOpen && alertAction === "delete"}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleAlertConfirm}
        title="Delete Release"
        description={`Are you sure you want to delete release "${selectedRelease?.name}"? This action cannot be undone.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        variant="warning"
      />

      {/* Set Latest confirmation */}
      <GenericAlert
        isOpen={alertOpen && alertAction === "setLatest"}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleAlertConfirm}
        title="Set as Latest"
        description={`Mark "${selectedRelease?.name}" as the latest release for channel "${selectedRelease?.channel}"? The previous latest will be unset.`}
        cancelLabel="Cancel"
        confirmLabel="Confirm"
        variant="warning"
      />
    </div>
  );
};

export default ReleasesPage;
